import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import AppLayout from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import UserFormDialog from "../components/UserFormDialog";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash2 } from "lucide-react";

export default function UserManagementPage() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("users.load_failed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleAdd = () => {
    setEditingUser(null);
    setDialogOpen(true);
  };

  const handleEdit = (u) => {
    setEditingUser(u);
    setDialogOpen(true);
  };

  const handleSubmit = async (payload) => {
    try {
      if (editingUser) {
        await api.put(`/users/${editingUser.id}`, payload);
        toast.success(t("users.updated"));
      } else {
        await api.post("/users", payload);
        toast.success(t("users.created"));
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("customers.operation_failed"));
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user "${u.email}"? This action cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(t("users.deleted"));
      fetchUsers();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("customers.delete_failed"));
    }
  };

  return (
    <AppLayout>
      <div data-testid="user-management-page" className="max-w-4xl space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">{t("nav.user_management")}</h1>
          <Button onClick={handleAdd} data-testid="add-user-button" className="rounded-lg bg-[#1677ff] hover:bg-[#0958d9] transition-colors">
            <UserPlus size={14} className="mr-1.5" /> {t("users.add")}
          </Button>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent bg-gray-50 border-b border-gray-200">
                <TableHead className="text-[12px] uppercase tracking-wide text-gray-700 font-bold">{t("users.email")}</TableHead>
                <TableHead className="text-[12px] uppercase tracking-wide text-gray-700 font-bold">{t("users.name")}</TableHead>
                <TableHead className="text-[12px] uppercase tracking-wide text-gray-700 font-bold">{t("users.role")}</TableHead>
                <TableHead className="text-[12px] uppercase tracking-wide text-gray-700 font-bold text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!loading && users.map((u) => (
                <TableRow key={u.id} className="border-b border-gray-50" data-testid={`user-row-${u.id}`}>
                  <TableCell className="text-sm text-gray-800">{u.email}</TableCell>
                  <TableCell className="text-sm text-gray-500">{u.name || "-"}</TableCell>
                  <TableCell>
                    <span className={`text-xs font-medium px-2 py-0.5 ${
                      u.role === "admin" ? "bg-brand-50 text-brand-600"
                      : u.role === "user_admin" ? "bg-indigo-50 text-indigo-700"
                      : u.role === "bh_product_admin" ? "bg-emerald-50 text-emerald-700"
                      : u.role === "sales_rep" ? "bg-amber-50 text-amber-700"
                      : "bg-gray-100 text-gray-600"
                    }`} data-testid={`user-role-badge-${u.id}`}>
                      {u.role === "admin" ? "Admin"
                        : u.role === "user_admin" ? "User Admin"
                        : u.role === "bh_product_admin" ? "BH Product Admin"
                        : u.role === "sales_rep" ? "Sales Rep"
                        : u.role === "sample_warehouse" ? "Sample Warehouse"
                        : u.role === "ci_admin" ? "CI Admin"
                        : "Staff"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    {/* iter 215 (2026-02): user_admin 对 admin 行的 Edit/Delete 全部禁用。 */}
                    {(() => {
                      const callerIsUserAdmin = currentUser?.role === "user_admin";
                      const targetIsAdmin = u.role === "admin";
                      const editDisabled = callerIsUserAdmin && targetIsAdmin;
                      const deleteDisabled = u.id === currentUser?.id || (callerIsUserAdmin && targetIsAdmin);
                      const editTitle = editDisabled ? "User Admin cannot edit an Admin account" : "Edit User";
                      const deleteTitle = u.id === currentUser?.id
                        ? "You cannot delete your own account"
                        : editDisabled ? "User Admin cannot delete an Admin account" : "Delete User";
                      return (
                        <div className="flex items-center justify-end gap-3">
                          <button
                            data-testid={`edit-user-${u.id}`}
                            onClick={() => !editDisabled && handleEdit(u)}
                            disabled={editDisabled}
                            title={editTitle}
                            className="text-gray-400 hover:text-brand-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            <Pencil size={14} strokeWidth={1.75} />
                          </button>
                          <button
                            data-testid={`delete-user-${u.id}`}
                            onClick={() => !deleteDisabled && handleDelete(u)}
                            disabled={deleteDisabled}
                            title={deleteTitle}
                            className="text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            <Trash2 size={14} strokeWidth={1.75} />
                          </button>
                        </div>
                      );
                    })()}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-sm text-gray-400 py-8">No users yet</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <UserFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        user={editingUser}
        onSubmit={handleSubmit}
      />
    </AppLayout>
  );
}
